import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useUnifiedAuth } from '../../context/UnifiedAuthContext';
import { useTheme } from '../../context/ThemeContext';
import { authStorage } from '../../services/storageService';

/**
 * StudentCompleteLogin - Handles the Magic Link callback
 * This page is opened when the student clicks the email link
 */
function StudentCompleteLogin() {
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [errorMessage, setErrorMessage] = useState('');
  
  const { completeMagicLinkSignIn, isAuthenticated, error } = useUnifiedAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get redirect URL from localStorage or default to dashboard
  const getRedirectUrl = () => {
    const stored = authStorage.get('studentRedirectAfterLogin');
    if (stored) {
      authStorage.remove('studentRedirectAfterLogin');
      return stored;
    }
    return '/student/dashboard';
  };
  
  useEffect(() => {
    const completeSignIn = async () => {
      try {
        const user = await completeMagicLinkSignIn();
        
        if (user) {
          setStatus('success');
          
          // Short delay to show success, then redirect
          setTimeout(() => {
            navigate(getRedirectUrl());
          }, 1500);
        } else {
          setStatus('error');
          setErrorMessage('Anmeldung fehlgeschlagen. Bitte versuche es erneut.');
        }
      } catch (err) {
        setStatus('error');
        setErrorMessage(err.message || 'Ein Fehler ist aufgetreten.');
      }
    };
    
    completeSignIn();
  }, []);
  
  // If already authenticated, redirect immediately
  useEffect(() => {
    if (isAuthenticated && status !== 'processing') {
      navigate(getRedirectUrl());
    }
  }, [isAuthenticated, status, navigate]);
  
  // Processing state
  if (status === 'processing') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-teal-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-xl w-full max-w-md text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-green-500 border-t-transparent mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
            Anmeldung wird abgeschlossen...
          </h1>
          <p className="text-gray-500 dark:text-gray-400">
            Bitte warte einen Moment.
          </p>
        </div>
      </div>
    );
  }
  
  // Success state
  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-teal-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-xl w-full max-w-md text-center">
          <div className="text-6xl mb-4">
            <svg className="w-24 h-24 mx-auto text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
            Erfolgreich angemeldet!
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            Du wirst weitergeleitet...
          </p>
          <div className="animate-pulse">
            <div className="h-2 bg-green-200 dark:bg-green-700 rounded-full overflow-hidden">
              <div className="h-full bg-green-500 animate-[progress_1.5s_ease-out]" 
                   style={{ width: '100%' }} />
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Error state
  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-xl w-full max-w-md text-center">
        <div className="text-6xl mb-4">
          <svg className="w-24 h-24 mx-auto text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
          Anmeldung fehlgeschlagen
        </h1>
        <p className="text-red-600 dark:text-red-400 mb-6">
          {errorMessage || error || 'Ein unbekannter Fehler ist aufgetreten.'}
        </p>
        
        <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-lg p-4 mb-6 text-left">
          <p className="text-sm text-amber-800 dark:text-amber-200 font-medium mb-2">
            Mögliche Ursachen:
          </p>
          <ul className="text-sm text-amber-700 dark:text-amber-300 space-y-1 list-disc list-inside">
            <li>Der Link ist abgelaufen (nach 1 Stunde)</li>
            <li>Der Link wurde bereits verwendet</li>
            <li>Die E-Mail-Adresse stimmt nicht überein</li>
          </ul>
        </div>
        
        <div className="space-y-3">
          <Link
            to="/?auth=true"
            className="block w-full py-3 px-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg theme-transition"
          >
            Neuen Link anfordern
          </Link>
          
          <Link
            to="/"
            className="block w-full py-2.5 px-4 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 theme-transition"
          >
            Zurück zur Startseite
          </Link>
        </div>
      </div>
    </div>
  );
}

export default StudentCompleteLogin;
